import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetMasterListQuery } from '../../store/master/hooks'
import { rhybusRListsHttp } from '../../api/rhybus-list-http'
import { ListNames, STATUS_CODE } from '../../config/configuration'
import { RhyBusButton } from '@rhybus/button'
import { RhyBusCard } from '@rhybus/card'
import { RhyBusDiscussion } from '@rhybus/discussion'
import { RhyBusFiles } from '@rhybus/files'
import { RhyBusPageHeader } from '@rhybus/header'
import { RhyBusProgressBar } from '@rhybus/progress-bar'
import { RhyBusTeamMembers } from '@rhybus/team-members'
import { addDiscussionToSharePoint, addReplyToSharePoint, addTeamMember, buildDetailProgressSteps, deleteTeamMember, fetchDiscussionsByGUID, fetchTeamMembersByGUID, getTeamMemberPeopleSuggestions, searchDiscussions, separateDiscussionsAndStatusNotes } from '../../services/detailview'
import {
    handleDetailAddFolder,
    handleDetailDeleteFiles,
    handleDetailDeleteSingleFile,
    handleDetailDownloadAllFiles,
    handleDetailDownloadFile,
    handleDetailUpdateFolderName,
    handleDetailUploadFiles,
    loadDetailFiles,
} from '../../shared/utility/fileHelpers'
import {
    DELETE_MSG,
    FILE_HINT_MSG,
    SELECT_ONE_ITEM,
    addDiscussionPopupConfig,
    replyPopupConfig,
    DETAIL_VIEW_NEW_ACTIONS,
    DETAIL_VIEW_NEW_DESCRIPTION,
    DETAIL_VIEW_NEW_HEADER,
    DETAIL_VIEW_NEW_HEADLINE_FIELDS,
    DETAIL_VIEW_NEW_PEOPLE_SUGGESTIONS,
    DETAIL_VIEW_NEW_STATUS_NOTES,
    DETAIL_VIEW_NEW_SUMMARY_FIELDS,
    DETAIL_VIEW_NEW_TEAM_MEMBERS,
    type DetailViewComment,
    type DetailViewFileItem,
    type DetailViewPerson,
} from '../../config/configuration'
import './detail-view.css'
import CancelButton from './grant-actionbuttons/CancelButton'
import BackButton from './grant-actionbuttons/BackButton'
import ApproveButton from './grant-actionbuttons/ApproveButton'
import DisapproveButton from './grant-actionbuttons/DisapproveButton'
import CloseGrantButton from './grant-actionbuttons/CloseGrantButton'
import AllocateFundsButton from './grant-actionbuttons/AllocateFundsButton'
import ImplementGrantButton from './grant-actionbuttons/ImplementGrantButton'
import { rhybusWhoHttp } from '../../api/rhybus-who-http'
import { UserContext } from '../../store/UserContextProvider'
import { UserDetailsContext } from '../../utils/user-context'
import PageOverlay from '../../utils/controls/pageoverLay/pageoverLay'
import { triggerCaseNotifications, ACTIVITY_ID } from '../../services/notificationUtils'

type LocalFolderItem = {
    ID: string
    Id: string
    title: string
    is_active: string
    count: number
    Files: DetailViewFileItem[]
}

const cloneFile = (file: DetailViewFileItem): DetailViewFileItem => ({ ...file })

const cloneComment = (comment: DetailViewComment): DetailViewComment => ({
    ...comment,
    ItemCreatedBy: { ...comment.ItemCreatedBy },
})

const clonePerson = (person: DetailViewPerson): DetailViewPerson => ({ ...person })

const filterComments = (comments: DetailViewComment[], query: string) => {
    const searchQuery = query.trim().toLowerCase()
    if (!searchQuery) return comments

    return comments.filter((comment) => {
        return [comment.Subject, comment.Comment, comment.CommentTo, comment.DocumentName, comment.ItemCreatedBy?.Title]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(searchQuery))
    })
}

const createFileItems = (uploadedFiles: any[]): DetailViewFileItem[] => {
    return uploadedFiles.map((file: any, index: number) => {
        const name = file?.name ?? file?.Name ?? `file-${index}`
        const url = file?.ServerRelativeUrl ?? (file?.name ? globalThis.URL.createObjectURL(file) : undefined)
        return {
            UniqueId: `${Date.now()}-${index}-${name}`,
            Name: name,
            Title: name,
            ServerRelativeUrl: url,
            Author: { Title: 'John Doe' },
            TimeCreated: new Date().toISOString(),
            ItemCreated: new Date().toISOString(),
            ItemCreatedBy: { Title: 'John Doe', Email: 'john.doe@example.com', Id: 1 },
        }
    })
}
export default function DetailViewNew() {
    const navigate = useNavigate()
    const { itemGuid } = useParams<{ itemGuid: string }>()
    const { data: allItems = [], refetch: refetchMasterList, isLoading: masterListLoading } = useGetMasterListQuery(undefined, { refetchOnMountOrArgChange: true })
    const { me } = useContext(UserContext)
    const { roles } = useContext(UserDetailsContext) as any
    const userRole = Array.isArray(roles) ? (roles as string[]).join(', ') : ''

    const grantData = useMemo(
        () => allItems.find((item: any) => item.ItemGUID === itemGuid || item.ItemGuid === itemGuid || item.CaseGUID === itemGuid) ?? null,
        [allItems, itemGuid]
    )


    const headlineFields = grantData ? [
        // { label: 'Case ID', value: (grantData as any).requestID || '—' },
        // { label: 'Status', value: (grantData as any).status || '—' },
    ] : DETAIL_VIEW_NEW_HEADLINE_FIELDS

    const summaryFields = grantData ? [
        { label: 'Case Type', value: (grantData as any).caseType || '—' },
        { label: 'Program Type', value: (grantData as any).programtype || '—' },
        { label: 'IA', value: (grantData as any).ia || '—' },
        { label: 'Country', value: (grantData as any).country || '—' },
        { label: 'Case Designator', value: (grantData as any).caseDesignator || '—' },
        { label: 'Start Fiscal Year', value: (grantData as any).fiscalYear || '—' },
        { label: 'End Fiscal Year', value: (grantData as any).endFiscalYear || '—' },
        { label: 'Submitted by', value: (grantData as any).submittedBy || '—' },
        { label: 'Submitted Date', value: (grantData as any).submitteddate || '—' },
        { label: 'User Case ID', value: (grantData as any).userCaseId || '—' },
    ] : DETAIL_VIEW_NEW_SUMMARY_FIELDS

    const pageTitle = (grantData as any)?.requestID || DETAIL_VIEW_NEW_HEADER.requestId
    const detailItemKey = useMemo(() => (
        (grantData as any)?.eKey
        || itemGuid
        || ''
    ), [grantData, itemGuid])

    const [grantLines, setGrantLines] = useState<{ lineNumber: string; regionalCenter: string }[]>([])
    const [historyItems, setHistoryItems] = useState<any[]>([])
    const caseGUID = useMemo(() => (grantData as any)?.CaseGUID ?? '', [grantData])

    const refreshHistory = useCallback(async () => {
        if (!caseGUID) return
        try {
            const historyList = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_HISTORY)
            const all = [...(await historyList.query().get<any>())]
            setHistoryItems(all.filter((h: any) => h.CaseGUID === caseGUID))
        } catch (err) {
            console.error('[fetchHistory]', err)
        }
    }, [caseGUID])

    useEffect(() => {
        refreshHistory()
    }, [refreshHistory])

    useEffect(() => {
        if (!caseGUID) return
        const fetchLines = async () => {
            try {
                const lineList = await rhybusRListsHttp().getList(ListNames.DSCA_CASE_MASTER_LINE)
                const all = [...(await lineList.query().get<any>())]
                setGrantLines(
                    all
                        .filter((l: any) => l.CaseGUID === caseGUID && !l.IsDeleted)
                        .map((l: any) => ({ lineNumber: l.UserCaseLine || '', regionalCenter: l.RegionalCenter || '' }))
                )
            } catch (err) {
                console.error('[fetchGrantLines]', err)
            }
        }
        fetchLines()
    }, [caseGUID])

    const isSubmittingDiscussionRef = useRef(false)
    const [isDiscussionLoading, setIsDiscussionLoading] = useState(false)
    const [discussionSearchText, setDiscussionSearchText] = useState('')
    const [files, setFiles] = useState<DetailViewFileItem[]>([])
    const [folders, setFolders] = useState<LocalFolderItem[]>([])
    const [isFilesLoading, setIsFilesLoading] = useState(false)
    const [loading, setLoading] = useState(false)
    const [fileError, setFileError] = useState<string | null>(null)
    const [folderError, setFolderError] = useState<string | null>(null)
    const [filesComponentKey, setFilesComponentKey] = useState(0)
    const [discussions, setDiscussions] = useState<DetailViewComment[]>([])
    const [allDiscussions, setAllDiscussions] = useState<any[]>([])
    const [statusNotes, setStatusNotes] = useState<any[]>([])
    const [peoplePickerSuggestions, setPeoplePickerSuggestions] = useState<DetailViewPerson[]>([])
    const [teamMembers, setTeamMembers] = useState<DetailViewPerson[]>(DETAIL_VIEW_NEW_TEAM_MEMBERS.map(clonePerson))
    const [key, setKey] = useState<any>()
    const [teamPeoplePickerSuggestions, setTeamPeoplePickerSuggestions] = useState<DetailViewPerson[]>([])
    const [currentTeamMembersListID, setCurrentTeamMembersListID] = useState<number | null>(null)

    const allFiles = useMemo(() => [...files, ...folders.flatMap((folder) => folder.Files)], [files, folders])
    const allFileNames = useMemo(() => allFiles.map((file) => file.Name).filter(Boolean), [allFiles])
    const currentUser = me?.profileName
    // Cache raw File objects — @rhybus/file-upload strips binary data before calling onUploadFiles
    const rawFileCache = useRef<Map<string, File>>(new Map())
    useEffect(() => {
        const capture = (e: Event) => {
            const input = e.target as HTMLInputElement
            if (input.type !== 'file' || !input.files?.length) return
            Array.from(input.files).forEach((f) => rawFileCache.current.set(`${f.name}_${f.size}`, f))
        }
        const captureDrop = (e: DragEvent) => {
            const files = e.dataTransfer?.files
            if (!files?.length) return
            Array.from(files).forEach((f) => rawFileCache.current.set(`${f.name}_${f.size}`, f))
        }
        document.addEventListener('change', capture, true)
        document.addEventListener('drop', captureDrop, true)
        return () => {
            document.removeEventListener('change', capture, true)
            document.removeEventListener('drop', captureDrop, true)
        }
    }, [])

    const loadFiles = () => loadDetailFiles(detailItemKey, setFiles, setFolders, setIsFilesLoading, setFileError, 'case-detail-view')

    useEffect(() => {
        if (!grantData) return
        loadFiles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detailItemKey, grantData])

    useEffect(() => {
        getTeamMembers()
    }, [caseGUID])

    useEffect(() => {
        if (!caseGUID) return
        getDiscussions()
    }, [caseGUID])
    const handleApproveSuccess = async () => {
        setLoading(true)
        await Promise.all([refetchMasterList(), refreshHistory(), getDiscussions()])
        setLoading(false)
    }

    const [isCanceled, setIsCanceled] = useState(false)
    const handleCancelSuccess = async () => {
        setIsCanceled(true)
        setLoading(true)
        await Promise.all([refetchMasterList(), refreshHistory(), getDiscussions()])
        setLoading(false)
    }

    const handleActionClick = (actionKey: string) => {
        if (actionKey === 'back' || actionKey === 'cancel') {
            navigate(-1)
        }
    }

    const handleDiscussionSearch = (event: { target: { value: string } }) => {
        const inputValue = event.target.value
        setDiscussionSearchText(inputValue)
        const filtered = searchDiscussions(inputValue.toLowerCase(), allDiscussions)
        const { discussions: disc, statusNotes: notes } = separateDiscussionsAndStatusNotes(filtered)
        setDiscussions(disc)
        setStatusNotes(notes)
    }

    const getDiscussions = async () => {
        const items = await fetchDiscussionsByGUID(caseGUID)
        setAllDiscussions(items)
        const { discussions: disc, statusNotes: notes } = separateDiscussionsAndStatusNotes(items)
        setDiscussions(disc)
        setStatusNotes(notes)
    }
    const handleAddDiscussion = async (discussionData: any) => {
        if (isSubmittingDiscussionRef.current) return
        isSubmittingDiscussionRef.current = true
        setIsDiscussionLoading(true)
        try {
            await addDiscussionToSharePoint(itemGuid, discussionData, me)
            await getDiscussions()
            const userCaseId = (grantData as any)?.userCaseId ?? caseGUID
            await triggerCaseNotifications(
                caseGUID,
                //`Case ${userCaseId} -A new discussion has been added by ${me?.profileName}`,
                `${me?.profileName} added a discussion comment on Grant Case ${userCaseId}.`,
                ACTIVITY_ID.CaseDiscussion,
                STATUS_CODE.WaitingforDSCAFunding,
                me?.email,
            )
        } finally {
            isSubmittingDiscussionRef.current = false
            setIsDiscussionLoading(false)
        }
    }

    const handleAddReply = async (replyData: any) => {
        if (isSubmittingDiscussionRef.current) return
        isSubmittingDiscussionRef.current = true
        setIsDiscussionLoading(true)
        try {
            await addReplyToSharePoint(itemGuid, replyData, me)
            await getDiscussions()
        } finally {
            isSubmittingDiscussionRef.current = false
            setIsDiscussionLoading(false)
        }
    }

    const handlePeoplePickerChange = async (value: string) => {
        if (!value) {
            setPeoplePickerSuggestions([])
            return
        }

        setPeoplePickerSuggestions(
            DETAIL_VIEW_NEW_PEOPLE_SUGGESTIONS.filter((person) => person.Title.toLowerCase().includes(value.toLowerCase()))
        )
    }

    const handleUploadFiles = (uploadedFiles: any[], folderName?: string) => {
        const rawFiles = uploadedFiles
            .map((f: any) => {
                // Prefer the cached raw File (captures binary data before the component strips it).
                // Fall back to f.file which @rhybus/file-upload now preserves on FileType objects.
                const cached = rawFileCache.current.get(`${f.name}_${f.size}`)
                if (cached instanceof File) return cached
                if (f.file instanceof File) return f.file
                return null
            })
            .filter((f): f is File => f instanceof File)
        if (!rawFiles.length) return
        // Supply the current eTag for each file that already exists so the server
        // can do a proper conditional update. getInfo is unreliable for existing blobs
        // and falls back to etag='' which causes "Unexpected error" on overwrite.
        const existingETagMap = new Map<string, string>()
        // Only search the correct location to avoid sending a root-blob eTag for a folder
        // blob (or vice versa) — the server rejects an eTag that doesn't match the path.
        const eTagSource: any[] = folderName
            ? (folders.find((folder: any) => folder.title === folderName)?.Files ?? [])
            : files
        rawFiles.forEach((f) => {
            const match = eTagSource.find((af: any) => af.Name === f.name)
            if (match?.eTag) existingETagMap.set(f.name, match.eTag)
        })
        return handleDetailUploadFiles(rawFiles, detailItemKey, setIsFilesLoading, setFileError, loadFiles, folderName, me, 'case-detail-view', existingETagMap)
            .then((uploadSucceeded) => {
                rawFiles.forEach((f) => rawFileCache.current.delete(`${f.name}_${f.size}`))
                // Optimistically add the uploaded files to local state so that
                // allFiles reflects them immediately even if loadFiles() returned
                // stale data (Azure Blob Storage eventual consistency). This ensures
                // the duplicate-detection check fires on the very next upload attempt.
                if (uploadSucceeded) {
                    if (!folderName) {
                        setFiles((prev: any[]) => {
                            const missing = rawFiles.filter((f) => !prev.some((p: any) => p.Name === f.name))
                            return missing.length ? [...prev, ...missing.map((f) => ({ Name: f.name, Title: f.name }))] : prev
                        })
                    } else {
                        setFolders((prev: any[]) =>
                            prev.map((folder: any) =>
                                folder.title === folderName
                                    ? {
                                        ...folder,
                                        Files: [
                                            ...folder.Files,
                                            ...rawFiles
                                                .filter((f: File) => !folder.Files.some((p: any) => p.Name === f.name))
                                                .map((f: File) => ({ Name: f.name, Title: f.name })),
                                        ],
                                    }
                                    : folder
                            )
                        )
                    }
                }
                // Remount RhyBusFiles to clear its internal pendingFilesRef, which the
                // library never resets after upload and causes stale files to appear the
                // next time the Add Files popup is opened.
                setFilesComponentKey((k) => k + 1)
            })
    }

    const handleDeleteFiles = (selectedFiles: Array<{ Name: string }>, folderName?: string) =>
        handleDetailDeleteFiles(selectedFiles, detailItemKey, setIsFilesLoading, setFileError, loadFiles, folderName, folders.map((f) => f.title), 'case-detail-view')

    const handleDownloadFile = (file: any) =>
        handleDetailDownloadFile(file, detailItemKey, 'case-detail-view')

    const handleDownloadAllFiles = (filesToDownload: any[]) =>
        handleDetailDownloadAllFiles(filesToDownload, detailItemKey, 'case-detail-view')

    const handleDeleteSingleFile = (fileToDelete: any) =>
        handleDetailDeleteSingleFile(fileToDelete, detailItemKey, setIsFilesLoading, setFileError, loadFiles, 'case-detail-view')

    const handleAddFolder = (folderName: string) => {
        setFolderError(null)
        const folderPath = `/${folderName}`
        setFolders((prev) => [
            ...prev,
            { ID: folderPath, Id: folderPath, title: folderName, is_active: 'true', count: 0, Files: [] },
        ].sort((a, b) => a.title.localeCompare(b.title)))
        void handleDetailAddFolder(folderName, detailItemKey, setIsFilesLoading, setFolderError, loadFiles, 'case-detail-view')
    }

    const handleUpdateFolderName = (oldName: string, newName: string) =>
        handleDetailUpdateFolderName(oldName, newName, setFolders)

    const getTeamMembers = async () => {
        const team = await fetchTeamMembersByGUID(itemGuid)
        if (team) {
            setTeamMembers(team.TeamMembers?.length > 0 ? team.TeamMembers : [])
            setKey(team)
            setCurrentTeamMembersListID(team?.eKey || team?.ID)
        } else {
            setTeamMembers([])
            setCurrentTeamMembersListID(null)
        }
    }

    const handleAddTeamMember = async (userToAdd: any, inputValues?: any) => {
        const validation = handleValidateUser(userToAdd)
        if (!validation.isValid) return
        setLoading(true)
        await addTeamMember(teamMembers, currentTeamMembersListID, caseGUID, userToAdd, grantData?.requestID, me, key?.eKey ?? '', key?.eTag ?? '')
        getTeamMembers()
        setLoading(false)
    }
    const handleTeamPeoplePickerChange = async (value: string) => {
        if (!value) {
            setTeamPeoplePickerSuggestions([])
            return
        }
        const suggestions = await getTeamMemberPeopleSuggestions(value)
        setTeamPeoplePickerSuggestions(suggestions)
    }
    const handleValidateUser = (userData: any) => {
        if (!userData) return { isValid: true, error: '' }

        // Check by email if available (object case)
        const email = userData?.EMail ?? userData?.email ?? userData?.Mail ?? ''
        if (email) {
            const alreadyExists = teamMembers.some((member: any) =>
                member?.EMail?.toLowerCase() === email.toLowerCase()
            )
            if (alreadyExists) {
                return { isValid: false, error: 'User is already a team member' }
            }
        }

        const userId = typeof userData === 'object'
            ? String(userData?.Id ?? userData?.ID ?? userData?.userId ?? userData?.LoginName ?? '').trim()
            : String(userData ?? '').trim()
        if (userId) {
            const alreadyExists = teamMembers.some((member: any) =>
                String(member?.ID ?? member?.Id ?? member?.userId ?? member?.LoginName ?? '').trim() === userId
            )
            if (alreadyExists) {
                return { isValid: false, error: 'User is already a team member' }
            }
        }

        return { isValid: true, error: '' }
    }

    const handleDeleteTeamMembers = async (selectedMembers: DetailViewPerson[]) => {
        setLoading(true)
        await deleteTeamMember(teamMembers, selectedMembers, currentTeamMembersListID, caseGUID, me, key?.eKey ?? '', key?.eTag ?? '')
        await getTeamMembers()
        setLoading(false)
    }
    const showBtn = currentUser?.trim().toLowerCase() !== grantData?.submittedBy?.trim().toLowerCase() && STATUS_CODE.WaitingforDSCAFunding === grantData?.statusCode

    const handleSearchTeamPeoplePicker = (event: any, setUserToAdd: (user: any) => void, setInputValues: (val: any) => void) => {
        // Convert the suggestion object to a user object with required properties
        const userObject = {
            Id: event?.Key ?? event?.rid ?? event?.userId ?? '',
            LoginName: event?.Key ?? event?.rid ?? event?.userId ?? event?.email ?? '',
            Email: event?.email ?? '',
            EMail: event?.email ?? '',
            Title: event?.DisplayText ?? event?.persona ?? event?.displayName ?? '',
            IsResolved: true,

        }
        setInputValues(userObject)
        setUserToAdd(userObject)
        setTeamPeoplePickerSuggestions([])
    }

    return (
        <main id="main-content">
            {(masterListLoading || loading || isFilesLoading || isDiscussionLoading) && <PageOverlay />}

            <RhyBusPageHeader
                pageHeaderClassName='detail-view-header'
                pageTitle=""
                headerLeftContent={
                    <div className="rb-page-header-title" id="pagetitles">
                        <h1 tabIndex={0} aria-live="polite" style={{ fontWeight: 700 }}>
                            <span className='darktext'>USER CASE ID:</span>
                            <span className='color-primary'>{(grantData as any)?.userCaseId || ''}</span>
                        </h1>
                    </div>
                }
                headerRightContent={
                    <div className='d-flex align-items-center gap-2 flex-wrap justify-content-end detail-view-actions detail-view-static-actions'>
                        {currentUser?.trim().toLowerCase() !== grantData?.submittedBy?.trim().toLowerCase() && STATUS_CODE.WaitingforInternalReview === grantData?.StatusCode && (
                            <ApproveButton
                                caseGUID={caseGUID}
                                caseID={(grantData as any)?.requestID ?? ''}
                                userCaseId={(grantData as any)?.userCaseId ?? ''}
                                currentStatusCode={Number((grantData as any)?.StatusCode) || null}
                                userRole={userRole}
                                me={me}
                                onSuccess={handleApproveSuccess}
                            />)}
                        {showBtn && (
                            <DisapproveButton />
                        )}
                        {showBtn && (
                            <AllocateFundsButton />
                        )}
                        {showBtn && (
                            <ImplementGrantButton />
                        )}
                        {showBtn && (
                            <CloseGrantButton />
                        )}

                        {!isCanceled && currentUser?.trim().toLowerCase() === grantData?.submittedBy?.trim().toLowerCase() && Number((grantData as any)?.StatusCode) !== STATUS_CODE.CANCELED && (<CancelButton
                            caseGUID={caseGUID}
                            caseID={(grantData as any)?.requestID ?? ''}
                            currentStatusCode={Number((grantData as any)?.StatusCode) || null}
                            userRole={userRole}
                            me={me}
                            onSuccess={handleCancelSuccess}
                        />)}
                        <BackButton />
                    </div>
                }
            />

            <div className='container'>
                <RhyBusCard
                    className='details-summary'
                    cardBody={
                        <>
                            <div className='detail-view-progress detail-view-static-progress'>
                                <RhyBusProgressBar
                                    showItems={6}
                                    isArrow={false}
                                    progressBarType='horizontal'
                                    steps={buildDetailProgressSteps(historyItems, grantData)}
                                    trigger='hover'
                                    responsive={[
                                        { breakpoint: 0, show: 2 },
                                        { breakpoint: 576, show: 4 },
                                        { breakpoint: 992, show: 6 },
                                    ]}
                                />
                            </div>
                        </>
                    }
                />

                <div className='row'>
                    <div className='col-12 col-lg-8'>
                        <RhyBusCard
                            isTitleBorder={true}
                            cardHeaderLeft={<div className='detail-view-card-title' tabIndex={0} aria-label='Case Information'>Case Information</div>}
                            cardHeaderRight={
                                <RhyBusButton
                                    label='Edit'
                                    leftIcon='icon-rb-pencil font-12'
                                    variant='text'
                                    size='md'
                                    className='inter-semibold font-13'
                                    onClick={() => navigate(`/createcase/${itemGuid}`)}
                                />
                            }
                            cardBody={
                                <>
                                    <div className='row g-3'>
                                        {summaryFields.map((field) => (
                                            <div tabIndex={0} aria-live='polite' className='col-12 col-md-3' key={field.label}>
                                                <div className='detail-view-field-label'>{field.label}</div>
                                                <div className='detail-view-field-value'>{field.value}</div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className='mt-3' tabIndex={0} aria-live='polite'>
                                        <div className='detail-view-field-label'>Description</div>
                                        <p className='detail-view-description-text mb-0'>{grantData?.Description || '—'}</p>
                                    </div>
                                    {/* Grant Lines */}
                                    <div className='mt-3' style={{ borderRadius: '10px' }}>
                                        <div style={{ backgroundColor: '#434D8A', padding: '10px 16px', borderRadius: '10px 10px 0 0' }}>
                                            <span className='inter-semibold font-14' style={{ color: '#fff' }} tabIndex={0} aria-live='polite'>Case Lines ({String(grantLines.length).padStart(2, '0')})</span>
                                        </div>
                                        <div style={{ border: '1px solid #D5D4F1', borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
                                            <table role='grid' aria-label='Grant Lines' style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                    <tr style={{ backgroundColor: '#F2F1FF' }}>
                                                        <th scope='col' style={{ padding: '10px 16px', fontWeight: 600, fontSize: '13px', borderBottom: '1px solid #D5D4F1', borderRight: '1px solid #D5D4F1', textAlign: 'center' }} tabIndex={0} aria-label='Line Number'>Line Number</th>
                                                        <th scope='col' style={{ padding: '10px 16px', fontWeight: 600, fontSize: '13px', borderBottom: '1px solid #D5D4F1', textAlign: 'center' }} tabIndex={0} aria-label='Regional Center'>Regional Center</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {grantLines.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={2} style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', color: '#6c757d' }} tabIndex={0} aria-label='No records found.'>No records found.</td>
                                                        </tr>
                                                    ) : (
                                                        grantLines.map((line, index) => (
                                                            <tr key={index} style={{ borderBottom: index < grantLines.length - 1 ? '1px solid #D5D4F1' : 'none' }}>
                                                                <td style={{ padding: '10px 16px', fontSize: '13px', textAlign: 'center', borderRight: '1px solid #D5D4F1' }} tabIndex={0} aria-label={String(line.lineNumber).padStart(3, '0')}>{String(line.lineNumber).padStart(3, '0')}</td>
                                                                <td style={{ padding: '10px 16px', fontSize: '13px', textAlign: 'center' }} tabIndex={0} aria-label={line.regionalCenter || 'Not Applicable'}>{line.regionalCenter || 'Not Applicable'}</td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            }
                        />

                        <div className='mt-3 discussions-section'>
                            <RhyBusDiscussion
                                allFileNames={allFileNames}
                                discussionsData={discussions}
                                statusNotesData={statusNotes}
                                onAddDiscussion={handleAddDiscussion}
                                onAddReply={handleAddReply}
                                peoplePickerSuggestions={peoplePickerSuggestions}
                                onPeoplePickerChange={handlePeoplePickerChange}
                                onResetPeoplePicker={() => setPeoplePickerSuggestions([])}
                                addDiscussionPopupConfig={addDiscussionPopupConfig}
                                replyConfig={replyPopupConfig}
                                isInternalUser={true}
                                showDiscussions={true}
                                showStatusNotes={true}
                                discusstionTabTitle='Discussions'
                                statusNotesTabTitle='Status Notes'
                                searchText={discussionSearchText}
                                onSearchChange={handleDiscussionSearch}
                                isBadge={true}
                                badgeType='text'
                                tabSize='xlarge'
                            />
                        </div>
                    </div>

                    <div className='col-12 col-lg-4'>
                        <div className='detail-view-sidebar-stack'>
                            {/* {isFilesLoading && (
                                <div className='detail-view-files-status' role='status' aria-live='polite'>
                                    Loading files...
                                </div>
                            )} */}
                            <RhyBusFiles
                                key={filesComponentKey}
                                isFiles={true}
                                isFolders={true}
                                isFolderFiles={true}
                                canAddFolder={true}
                                canAddFileInFolder={true}
                                popoverSize='md'
                                isCheckbox={true}
                                isDownload={true}
                                isDownloadAll={true}
                                isGlobalDelete={true}
                                isFileLevelDelete={true}
                                isFileLevelDownload={true}
                                data={{ files, Folders: folders, Count: allFiles }}
                                canAddFile={true}
                                canDelete={true}
                                label='Files'
                                onUploadFiles={handleUploadFiles}
                                onAddFolder={handleAddFolder}
                                onDeleteFiles={handleDeleteFiles}
                                DELETE_MSG={DELETE_MSG}
                                SELECT_ONE_ITEM={SELECT_ONE_ITEM}
                                onDownload={handleDownloadFile}
                                onDownloadAll={handleDownloadAllFiles}
                                onDelete={handleDeleteSingleFile}
                                addLabel='Add'
                                downloadLabel='Download'
                                downloadAllLabel='Download All'
                                addFolderLabel='Add Folder'
                                note='Screenshots need to be saved locally before uploading into system.'
                                FILE_HINT_MSG={FILE_HINT_MSG}
                                fileError={fileError}
                                folderError={folderError}
                                titleIcon="icon-rb-files-blank-fill"
                                onUpdateFolderName={handleUpdateFolderName}
                            />

                            <RhyBusTeamMembers
                                TeamMembersData={teamMembers}
                                peoplePickerData={teamPeoplePickerSuggestions}
                                onAddTeamMember={handleAddTeamMember}
                                onDeleteTeamMembers={handleDeleteTeamMembers}
                                onValidateUser={handleValidateUser}
                                onSearchPeoplePicker={handleSearchTeamPeoplePicker}
                                onPeoplePickerChange={handleTeamPeoplePickerChange}
                                tooltipsData={{ 'Team Members': 'Project and review participants' }}
                                teammembersIcon='icon-rb-poc font-24'
                                IsBadgeType='text'
                                labels={{
                                    teamMembers: 'Team Members',
                                    addButton: 'Add',
                                    popoverTitle: 'Add Team Member',
                                    okButton: 'OK',
                                    cancelButton: 'Cancel',
                                    deleteButton: 'Delete',
                                    teamMemberField: 'Team Member',
                                    teamMemberFieldTooltip: 'Select a person to add as a team member',
                                    pleaseEnterTeamMember: 'Please enter Team Member',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </main>
    )
}
